import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  PlusIcon, 
  MagnifyingGlassIcon, 
  FunnelIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  DocumentArrowDownIcon,
  KeyIcon,
  ComputerDesktopIcon,
  ClockIcon,
  UserIcon,
  CurrencyDollarIcon,
  CogIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { Button, Input, Card, CardContent, CardHeader, CardTitle, Modal, Badge, ClientSearchSelect, SuccessNotification, Pagination } from '../../components/ui';
import { licenseService } from '../../services/licenseService';
import { clientService } from '../../services/clientService';
import { softwareService } from '../../services/softwareService';
import { licenseTypeService } from '../../services/licenseTypeService';
import { formatDate } from '../../utils/formatters';
import { LICENSE_STATUS, SUCCESS_MESSAGES } from '../../constants';
import {
  isApiRequestError,
  drfErrorsToFormState,
  buildFormErrorBanner,
  formatApiErrorForUser,
} from '../../utils/apiErrors';

export const LicensesPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLicense, setSelectedLicense] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [createdLicense, setCreatedLicense] = useState(null);
  const [createdLicenseClient, setCreatedLicenseClient] = useState(null);
  const [createdLicenseSoftware, setCreatedLicenseSoftware] = useState(null);
  const [editingLicense, setEditingLicense] = useState(null);
  const [editForm, setEditForm] = useState({
    client: '',
    software: '',
    license_type: '',
    status: 'active',
    max_installations: 5,
    perpetual_validity: false,
    expiry_days: 365,
    price: 100.00,
    original_price: '',
    discount_percentage: 0,
    promotion_name: '',
    promotion_description: '',
    notes: ''
  });
  const [editFormErrors, setEditFormErrors] = useState({});
  const [filters, setFilters] = useState({
    status: '',
    license_type: '',
    is_active: ''
  });

  // Form states
  const [createForm, setCreateForm] = useState({
    client: '',
    software: '',
    license_type: '',
    status: 'active',
    max_installations: 5,
    perpetual_validity: false,
    expiry_days: 365,
    price: 100.00,
    original_price: '',
    discount_percentage: 0,
    promotion_name: '',
    promotion_description: '',
    notes: ''
  });
  const [createFormErrors, setCreateFormErrors] = useState({});
  const [createFormBanner, setCreateFormBanner] = useState('');
  const [editFormBanner, setEditFormBanner] = useState('');
  const [listActionError, setListActionError] = useState('');

  const queryClient = useQueryClient();

  // Fetch licenses
  const { data: licensesData, isLoading, error } = useQuery({
    queryKey: ['licenses', searchTerm, filters],
    queryFn: () => licenseService.getLicenses({ search: searchTerm, ...filters }),
  });

  // Extract licenses and pagination info from response
  const allLicenses = licensesData?.results || licensesData || [];
  
  // Client-side pagination (temporary until backend implements it)
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const licenses = allLicenses.slice(startIndex, endIndex);
  
  const paginationInfo = {
    count: allLicenses.length,
    total_pages: Math.ceil(allLicenses.length / itemsPerPage),
    current_page: currentPage,
    page_size: itemsPerPage
  };

  // Fetch clients for form
  const { data: clients } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientService.getClients({}),
  });

  // Fetch software for form
  const { data: software } = useQuery({
    queryKey: ['software'],
    queryFn: () => softwareService.getSoftware({}),
  });

  // Fetch license types for dropdown
  const { data: licenseTypes } = useQuery({
    queryKey: ['licenseTypes'],
    queryFn: () => licenseTypeService.getLicenseTypes({}),
  });

  // Create license mutation
  const createLicenseMutation = useMutation({
    mutationFn: licenseService.createLicense,
    onSuccess: (data) => {
      queryClient.invalidateQueries(['licenses']);
      setCreateFormBanner('');
      
      // Guardar información del cliente y software antes de resetear el formulario
      const selectedClient = clients?.find(c => c.id === parseInt(createForm.client));
      const selectedSoftware = software?.find(s => s.id === parseInt(createForm.software));
      
      
      setCreatedLicense(data);
      setCreatedLicenseClient(selectedClient);
      setCreatedLicenseSoftware(selectedSoftware);
      
      setIsCreateModalOpen(false);
      setIsSuccessModalOpen(true);
      resetCreateForm();
    },
    onError: (error) => {
      console.error('Error creating license:', error);
      if (isApiRequestError(error)) {
        const { fieldErrors } = drfErrorsToFormState(error.body?.errors);
        const mapped = { ...fieldErrors };
        if (mapped.license_type_config && !mapped.license_type) {
          mapped.license_type = mapped.license_type_config;
        }
        setCreateFormErrors(mapped);
        setCreateFormBanner(buildFormErrorBanner(error));
      } else {
        setCreateFormBanner(error?.message || 'No se pudo crear la licencia');
      }
    },
  });

  // Update license mutation
  const updateLicenseMutation = useMutation({
    mutationFn: ({ id, data }) => licenseService.updateLicense(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['licenses']);
      setIsEditModalOpen(false);
      setEditingLicense(null);
      setEditFormBanner('');
      // No llamar resetEditForm() aquí: vacía el formulario a valores por defecto (p. ej. vigencia 365)
      // mientras el modal aún está visible un instante. Se resetea al cerrar con Cancelar o al abrir otra edición.
      console.log(SUCCESS_MESSAGES.LICENSE_UPDATED);
    },
    onError: (error) => {
      console.error('Error updating license:', error);
      if (isApiRequestError(error)) {
        const { fieldErrors } = drfErrorsToFormState(error.body?.errors);
        const mapped = { ...fieldErrors };
        if (mapped.license_type_config && !mapped.license_type) {
          mapped.license_type = mapped.license_type_config;
        }
        setEditFormErrors(mapped);
        setEditFormBanner(buildFormErrorBanner(error));
      } else {
        setEditFormBanner(error?.message || 'No se pudo actualizar la licencia');
      }
    },
  });

  // Delete license mutation
  const deleteLicenseMutation = useMutation({
    mutationFn: licenseService.deleteLicense,
    onSuccess: () => {
      queryClient.invalidateQueries(['licenses']);
      setListActionError('');
      console.log(SUCCESS_MESSAGES.LICENSE_DELETED);
    },
    onError: (error) => {
      setListActionError(
        isApiRequestError(error) ? formatApiErrorForUser(error) : error?.message || 'No se pudo eliminar la licencia'
      );
    },
  });

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Reset pagination when filters change
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Form handlers
  const handleCreateFormChange = (field, value) => {
    setCreateForm(prev => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (createFormErrors[field]) {
      setCreateFormErrors(prev => ({ ...prev, [field]: null }));
    }
    if (createFormBanner) setCreateFormBanner('');
  };

  const resetCreateForm = () => {
    setCreateForm({
      client: '',
      software: '',
      license_type: '',
      status: 'active',
      max_installations: 5,
      perpetual_validity: false,
      expiry_days: 365,
      price: 100.00,
      original_price: '',
      discount_percentage: 0,
      promotion_name: '',
      promotion_description: '',
      notes: ''
    });
    setCreateFormErrors({});
    setCreateFormBanner('');
  };

  const handleCreateLicense = (e) => {
    e.preventDefault();
    setCreateFormErrors({});
    setCreateFormBanner('');
    
    // Validar campos obligatorios
    if (!createForm.price || createForm.price <= 0) {
      setCreateFormErrors({ price: 'El precio es obligatorio y debe ser mayor a 0' });
      return;
    }
    
    if (!createForm.license_type) {
      setCreateFormErrors({ license_type: 'Debe seleccionar un tipo de licencia' });
      return;
    }
    
    // Preparar datos para envío
    const formData = {
      ...createForm,
      perpetual_validity: Boolean(createForm.perpetual_validity),
      // Asegurar que los valores numéricos sean correctos
      price: parseFloat(createForm.price) || 0,
      original_price: createForm.original_price ? parseFloat(createForm.original_price) : null,
      discount_percentage: parseFloat(createForm.discount_percentage) || 0,
      max_installations: parseInt(createForm.max_installations) || 1,
      // Solo usar license_type_config (el tipo de licencia definido por el usuario)
      license_type_config: createForm.license_type?.id || createForm.license_type
    };
    if (formData.perpetual_validity) {
      delete formData.expiry_days;
    } else {
      formData.expiry_days = parseInt(createForm.expiry_days, 10) || 365;
    }
    
    // Eliminar el campo license_type hardcodeado
    delete formData.license_type;
    
    // Limpiar campos undefined
    Object.keys(formData).forEach(key => {
      if (formData[key] === undefined) {
        delete formData[key];
      }
    });
    
    createLicenseMutation.mutate(formData);
  };

  const handleCreateModalClose = () => {
    setIsCreateModalOpen(false);
    resetCreateForm();
  };

  // Edit form functions
  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (editFormErrors[field]) {
      setEditFormErrors(prev => ({ ...prev, [field]: null }));
    }
    if (editFormBanner) setEditFormBanner('');
  };

  const resetEditForm = () => {
    setEditForm({
      client: '',
      software: '',
      license_type: '',
      status: 'active',
      max_installations: 5,
      perpetual_validity: false,
      expiry_days: 365,
      price: 100.00,
      original_price: '',
      discount_percentage: 0,
      promotion_name: '',
      promotion_description: '',
      notes: ''
    });
    setEditFormErrors({});
    setEditFormBanner('');
  };

  /** La API suele devolver FKs como número; a veces como objeto { id }. Unificar a string para los formularios. */
  const fkToFormValue = (field) => {
    if (field == null || field === '') return '';
    if (typeof field === 'object' && field !== null && 'id' in field) {
      return String(field.id);
    }
    return String(field);
  };

  const populateEditForm = (license) => {
    const licenseTypeId =
      license.license_type_config_id ??
      (license.license_type_config &&
      typeof license.license_type_config === 'object'
        ? license.license_type_config.id
        : license.license_type_config);

    const perpetual = Boolean(license.perpetual_validity) || !license.expiry_date;
    let expiryDays = 365;
    if (!perpetual && license.expiry_date && license.issued_date) {
      const exp = new Date(license.expiry_date).getTime();
      const iss = new Date(license.issued_date).getTime();
      const d = Math.round((exp - iss) / 86400000);
      if (d > 0) expiryDays = d;
    }

    setEditForm({
      client: fkToFormValue(license.client),
      software: fkToFormValue(license.software),
      license_type: fkToFormValue(licenseTypeId),
      status: license.status || 'active',
      max_installations: license.max_installations || 5,
      perpetual_validity: perpetual,
      expiry_days: expiryDays,
      price: license.price || 100.00,
      original_price: license.original_price || '',
      discount_percentage: license.discount_percentage || 0,
      promotion_name: license.promotion_name || '',
      promotion_description: license.promotion_description || '',
      notes: license.notes || ''
    });
  };

  const handleEditLicenseSubmit = (e) => {
    e.preventDefault();
    setEditFormErrors({});
    setEditFormBanner('');
    
    // Validar campos obligatorios
    if (!editForm.price || editForm.price <= 0) {
      setEditFormErrors({ price: 'El precio es obligatorio y debe ser mayor a 0' });
      return;
    }
    
    if (!editForm.license_type) {
      setEditFormErrors({ license_type: 'Debe seleccionar un tipo de licencia' });
      return;
    }
    
    // Preparar datos para envío
    const formData = {
      ...editForm,
      perpetual_validity: Boolean(editForm.perpetual_validity),
      // Asegurar que los valores numéricos sean correctos
      price: parseFloat(editForm.price) || 0,
      original_price: editForm.original_price ? parseFloat(editForm.original_price) : null,
      discount_percentage: parseFloat(editForm.discount_percentage) || 0,
      max_installations: parseInt(editForm.max_installations) || 1,
      // Solo usar license_type_config (el tipo de licencia definido por el usuario)
      license_type_config: parseInt(editForm.license_type)
    };
    if (formData.perpetual_validity) {
      delete formData.expiry_days;
    } else {
      formData.expiry_days = parseInt(editForm.expiry_days, 10) || 365;
    }
    
    // Eliminar el campo license_type hardcodeado
    delete formData.license_type;

    Object.keys(formData).forEach(key => {
      if (formData[key] === undefined) {
        delete formData[key];
      }
    });

    updateLicenseMutation.mutate({ id: editingLicense.id, data: formData });
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setEditingLicense(null);
    resetEditForm();
  };

  const handleDeleteLicense = (licenseId) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta licencia?')) {
      deleteLicenseMutation.mutate(licenseId);
    }
  };

  const handleViewLicense = (license) => {
    setSelectedLicense(license);
    setIsViewModalOpen(true);
  };

  const handleEditLicense = (license) => {
    setEditFormErrors({});
    setEditFormBanner('');
    setEditingLicense(license);
    populateEditForm(license);
    setIsEditModalOpen(true);
  };

  const openCreateLicenseModal = () => {
    setListActionError('');
    setIsCreateModalOpen(true);
  };

  // Helper functions
  const getStatusLabel = (status) => {
    const statusObj = LICENSE_STATUS.find(s => s.value === status);
    return statusObj ? statusObj.label : status;
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'expired': return 'danger';
      case 'suspended': return 'warning';
      case 'cancelled': return 'danger';
      default: return 'secondary';
    }
  };

  const getTypeLabel = (license) => {
    // Usar directamente el campo license_type que ahora contiene el nombre del tipo definido por el usuario
    return license.license_type || 'Sin tipo';
  };

  const getTypeBadgeVariant = (license) => {
    // Usar un color basado en el ID del LicenseType para consistencia
    const typeId = license.license_type_config || 0;
    const colors = ['info', 'success', 'warning', 'danger', 'secondary', 'primary'];
    return colors[typeId % colors.length];
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Hace un momento';
    if (diffInSeconds < 3600) return `Hace ${Math.floor(diffInSeconds / 60)} min`;
    if (diffInSeconds < 86400) return `Hace ${Math.floor(diffInSeconds / 3600)} h`;
    if (diffInSeconds < 2592000) return `Hace ${Math.floor(diffInSeconds / 86400)} días`;
    if (diffInSeconds < 31536000) return `Hace ${Math.floor(diffInSeconds / 2592000)} meses`;
    return `Hace ${Math.floor(diffInSeconds / 31536000)} años`;
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Licencias</h1>
        </div>
        <div className="grid gap-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-20 bg-gray-200 animate-pulse rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Licencias</h1>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <p className="text-red-600">Error al cargar las licencias</p>
              <p className="text-sm text-gray-500 mt-2">{error.message}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Licencias</h1>
          <p className="text-gray-600">Gestiona las licencias de software</p>
        </div>
        <Button onClick={openCreateLicenseModal}>
          <PlusIcon className="h-4 w-4 mr-2" />
          Nueva Licencia
        </Button>
      </div>

      {listActionError && (
        <div
          role="alert"
          className="rounded-md bg-red-50 border border-red-200 p-4 text-red-800 text-sm flex justify-between items-start gap-4"
        >
          <span className="whitespace-pre-wrap">{listActionError}</span>
          <button
            type="button"
            className="text-red-700 hover:text-red-900 font-medium shrink-0"
            onClick={() => setListActionError('')}
          >
            Cerrar
          </button>
        </div>
      )}

      {/* Filters and Search */}
      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="md:col-span-2">
              <Input
                placeholder="Buscar licencias..."
                value={searchTerm}
                onChange={handleSearch}
                icon={<MagnifyingGlassIcon className="h-4 w-4" />}
              />
            </div>
            <div>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos los estados</option>
                {LICENSE_STATUS.map(status => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <select
                value={filters.license_type}
                onChange={(e) => handleFilterChange('license_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos los tipos</option>
                {(licenseTypes?.results || licenseTypes || []).map(type => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <select
                value={filters.is_active}
                onChange={(e) => handleFilterChange('is_active', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todas</option>
                <option value="true">Activas</option>
                <option value="false">Inactivas</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Licenses Table */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Lista de Licencias</CardTitle>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">
                {licenses ? `${licenses.length} licencia${licenses.length !== 1 ? 's' : ''}` : '0 licencias'}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.print()}
                className="text-gray-500 hover:text-gray-700"
              >
                <DocumentArrowDownIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {licenses && licenses.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-4 px-6 font-semibold text-gray-900">
                      Licencia
                    </th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-900">
                      Cliente
                    </th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-900">
                      Software
                    </th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-900">
                      Tipo
                    </th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-900">
                      Estado
                    </th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-900">
                      Instalaciones
                    </th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-900">
                      Expiración
                    </th>
                    <th className="text-right py-4 px-6 font-semibold text-gray-900">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {licenses.map((license) => (
                    <tr key={license.id} className="hover:bg-gray-50 transition-colors">
                      {/* Licencia */}
                      <td className="py-4 px-6">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <KeyIcon className="h-5 w-5 text-blue-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {license.license_key}
                            </div>
                            <div className="text-sm text-gray-500">
                              {formatDate(license.issued_date)}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Cliente */}
                      <td className="py-4 px-6">
                        <div className="text-sm text-gray-900">
                          {license.client_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {license.client_email}
                        </div>
                      </td>

                      {/* Software */}
                      <td className="py-4 px-6">
                        <div className="text-sm text-gray-900">
                          {license.software_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          v{license.software_version}
                        </div>
                      </td>

                      {/* Tipo */}
                      <td className="py-4 px-6">
                        <Badge 
                          variant={getTypeBadgeVariant(license)}
                          className="text-xs"
                        >
                          {getTypeLabel(license)}
                        </Badge>
                      </td>

                      {/* Estado */}
                      <td className="py-4 px-6">
                        <Badge variant={getStatusBadgeVariant(license.status)}>
                          {getStatusLabel(license.status)}
                        </Badge>
                      </td>

                      {/* Instalaciones */}
                      <td className="py-4 px-6">
                        <div className="flex items-center">
                          <ComputerDesktopIcon className="h-4 w-4 text-gray-400 mr-1" />
                          <span className="text-sm font-medium text-gray-900">
                            {license.current_installations}/{license.max_installations}
                          </span>
                        </div>
                        {license.remaining_installations > 0 && (
                          <div className="text-xs text-gray-500">
                            {license.remaining_installations} restantes
                          </div>
                        )}
                      </td>

                      {/* Expiración */}
                      <td className="py-4 px-6">
                        {license.perpetual_validity || !license.expiry_date ? (
                          <span className="text-sm font-medium text-gray-800">Indefinida</span>
                        ) : (
                          <div>
                            <div className="text-sm text-gray-900">
                              {formatDate(license.expiry_date)}
                            </div>
                            <div className="text-sm text-gray-500">
                              {getTimeAgo(license.expiry_date)}
                            </div>
                          </div>
                        )}
                      </td>

                      {/* Acciones */}
                      <td className="py-4 px-6">
                        <div className="flex justify-end space-x-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewLicense(license)}
                            className="text-gray-400 hover:text-blue-600"
                            title="Ver detalles"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditLicense(license)}
                            className="text-gray-400 hover:text-green-600"
                            title="Editar licencia"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteLicense(license.id)}
                            className="text-gray-400 hover:text-red-600"
                            title="Eliminar licencia"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="mx-auto h-12 w-12 text-gray-400">
                <KeyIcon className="h-12 w-12" />
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No hay licencias</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || Object.values(filters).some(f => f) 
                  ? 'Intenta ajustar los filtros de búsqueda'
                  : 'Comienza agregando tu primera licencia'
                }
              </p>
              {!searchTerm && !Object.values(filters).some(f => f) && (
                <div className="mt-6">
                  <Button onClick={openCreateLicenseModal}>
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Nueva Licencia
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {licenses && licenses.length > 0 && (
        <div className="flex items-center justify-between mt-6">
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-700">
              Mostrando {startIndex + 1} a {Math.min(endIndex, paginationInfo.count)} de {paginationInfo.count} licencias
            </span>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
            <span className="text-sm text-gray-500">por página</span>
          </div>
          <Pagination
            currentPage={paginationInfo.current_page || currentPage}
            totalPages={paginationInfo.total_pages || 1}
            onPageChange={handlePageChange}
            totalItems={paginationInfo.count || licenses.length}
            itemsPerPage={itemsPerPage}
            showInfo={true}
          />
        </div>
      )}

      {/* Create License Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={handleCreateModalClose}
        title="Nueva Licencia"
      >
        <form onSubmit={handleCreateLicense} className="p-6">
          {createFormBanner && (
            <div
              role="alert"
              className="mb-4 rounded-md bg-red-50 border border-red-200 p-3 text-red-800 text-sm whitespace-pre-wrap"
            >
              {createFormBanner}
            </div>
          )}
          <div className="space-y-6">
            {/* 1. Cliente, software y tipo (catálogo) */}
            <div className="border-b border-gray-200 pb-4">
              <div className="flex items-center space-x-2 mb-3">
                <UserIcon className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-medium text-gray-900">1. Cliente, software y tipo del catálogo</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Client */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cliente *
                  </label>
                  <ClientSearchSelect
                    value={createForm.client}
                    onChange={(value) => handleCreateFormChange('client', value)}
                    placeholder="Buscar cliente..."
                    error={createFormErrors.client}
                    required
                  />
                </div>

                {/* Software */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Software *
                  </label>
                  <select
                    value={createForm.software}
                    onChange={(e) => handleCreateFormChange('software', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Seleccionar software</option>
                    {(software?.results || software || []).map(sw => (
                      <option key={sw.id} value={String(sw.id)}>
                        {sw.name} v{sw.version}
                      </option>
                    ))}
                  </select>
                  {createFormErrors.software && (
                    <p className="text-red-500 text-sm mt-1">{createFormErrors.software}</p>
                  )}
                </div>

                {/* Tipo del catálogo (plantilla) */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de licencia (catálogo) *
                  </label>
                  <select
                    value={createForm.license_type}
                    onChange={(e) => handleCreateFormChange('license_type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Seleccionar tipo de licencia</option>
                    {licenseTypes?.results?.map(type => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    )) || licenseTypes?.map(type => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-2 text-sm text-gray-600 bg-slate-50 border border-slate-100 rounded-md p-2">
                    El <strong>tipo</strong> solo define la etiqueta del catálogo. <strong>Precio</strong>,{' '}
                    <strong>promos</strong>, <strong>vigencia</strong> e <strong>instalaciones</strong> son de esta
                    licencia. Cambiar el tipo en el catálogo no modifica licencias ya emitidas.
                  </p>
                </div>
              </div>
            </div>

            {/* 2. Valores que quedan guardados en la licencia */}
            <div className="border-b border-gray-200 pb-4">
              <div className="flex items-center space-x-2 mb-3">
                <CurrencyDollarIcon className="h-5 w-5 text-green-600" />
                <h3 className="text-lg font-medium text-gray-900">
                  2. Contrato de esta licencia (precio, promos y vigencia)
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Precio *
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    value={createForm.price}
                    onChange={(e) => handleCreateFormChange('price', parseFloat(e.target.value))}
                    placeholder="0.00"
                    error={createFormErrors.price}
                    required
                  />
                </div>

                {/* Original Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Precio Original
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    value={createForm.original_price}
                    onChange={(e) => handleCreateFormChange('original_price', parseFloat(e.target.value))}
                    placeholder="0.00"
                    error={createFormErrors.original_price}
                  />
                </div>

                {/* Discount Percentage */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descuento (%)
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={createForm.discount_percentage}
                    onChange={(e) => handleCreateFormChange('discount_percentage', parseFloat(e.target.value))}
                    placeholder="0"
                    error={createFormErrors.discount_percentage}
                  />
                </div>

                {/* Promotion Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre de Promoción
                  </label>
                  <Input
                    type="text"
                    value={createForm.promotion_name}
                    onChange={(e) => handleCreateFormChange('promotion_name', e.target.value)}
                    placeholder="Ej: Black Friday 2024"
                    error={createFormErrors.promotion_name}
                  />
                </div>

                {/* Promotion Description */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción de Promoción
                  </label>
                  <textarea
                    value={createForm.promotion_description}
                    onChange={(e) => handleCreateFormChange('promotion_description', e.target.value)}
                    placeholder="Descripción de la promoción..."
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            <div className="border-b border-gray-200 pb-4">
              <div className="flex items-center space-x-2 mb-3">
                <CogIcon className="h-5 w-5 text-purple-600" />
                <h3 className="text-lg font-medium text-gray-900">Estado, cupos y vigencia</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estado *
                  </label>
                  <select
                    value={createForm.status}
                    onChange={(e) => handleCreateFormChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    {LICENSE_STATUS.map(status => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Max Installations */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Máximo de Instalaciones *
                  </label>
                  <Input
                    type="number"
                    value={createForm.max_installations}
                    onChange={(e) => handleCreateFormChange('max_installations', parseInt(e.target.value))}
                    placeholder="5"
                    error={createFormErrors.max_installations}
                    required
                    min="1"
                  />
                </div>

                <div className="md:col-span-2 space-y-3">
                  <div className="flex items-start gap-2">
                    <input
                      id="create-perpetual-validity"
                      type="checkbox"
                      checked={createForm.perpetual_validity}
                      onChange={(e) =>
                        handleCreateFormChange('perpetual_validity', e.target.checked)
                      }
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="create-perpetual-validity" className="text-sm text-gray-800">
                      <span className="font-medium">Vigencia indefinida</span>
                      <span className="block text-gray-500 font-normal mt-0.5">
                        Sin fecha de vencimiento (mismo contacto para varias sucursales u ofertas de por vida).
                      </span>
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Vigencia (días desde hoy)
                    </label>
                    <Input
                      type="number"
                      value={createForm.expiry_days}
                      onChange={(e) =>
                        handleCreateFormChange(
                          'expiry_days',
                          parseInt(e.target.value, 10) || 1
                        )
                      }
                      placeholder="365"
                      error={createFormErrors.expiry_days}
                      min="1"
                      disabled={createForm.perpetual_validity}
                      className={createForm.perpetual_validity ? 'opacity-60' : ''}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {createForm.perpetual_validity
                        ? 'Desactivá la casilla anterior para fijar una fecha de expiración desde hoy.'
                        : 'Parte del contrato: al crear se guarda la fecha de expiración desde hoy.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Notas */}
            <div>
              <div className="flex items-center space-x-2 mb-3">
                <DocumentTextIcon className="h-5 w-5 text-gray-600" />
                <h3 className="text-lg font-medium text-gray-900">Notas Adicionales</h3>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notas
                </label>
                <textarea
                  value={createForm.notes}
                  onChange={(e) => handleCreateFormChange('notes', e.target.value)}
                  placeholder="Notas adicionales sobre la licencia"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="2"
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 mt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={handleCreateModalClose}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={createLicenseMutation.isPending}
            >
              {createLicenseMutation.isPending ? 'Creando...' : 'Crear Licencia'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit License Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={handleEditModalClose}
        title="Editar Licencia"
      >
        <form onSubmit={handleEditLicenseSubmit} className="p-6">
          {editFormBanner && (
            <div
              role="alert"
              className="mb-4 rounded-md bg-red-50 border border-red-200 p-3 text-red-800 text-sm whitespace-pre-wrap"
            >
              {editFormBanner}
            </div>
          )}
          <div className="space-y-6">
            <div className="border-b border-gray-200 pb-4">
              <div className="flex items-center space-x-2 mb-3">
                <UserIcon className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-medium text-gray-900">1. Cliente, software y tipo del catálogo</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cliente *
                  </label>
                  <ClientSearchSelect
                    value={editForm.client}
                    onChange={(value) => handleEditFormChange('client', value)}
                    placeholder="Buscar cliente..."
                    error={editFormErrors.client}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Software *
                  </label>
                  <select
                    value={editForm.software}
                    onChange={(e) => handleEditFormChange('software', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Seleccionar software</option>
                    {(software?.results || software || []).map(sw => (
                      <option key={sw.id} value={String(sw.id)}>
                        {sw.name} v{sw.version}
                      </option>
                    ))}
                  </select>
                  {editFormErrors.software && (
                    <p className="text-red-500 text-sm mt-1">{editFormErrors.software}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de licencia (catálogo) *
                  </label>
                  <select
                    value={editForm.license_type}
                    onChange={(e) => handleEditFormChange('license_type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Seleccionar tipo de licencia</option>
                    {licenseTypes?.results?.map(type => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    )) || licenseTypes?.map(type => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-2 text-sm text-gray-600 bg-slate-50 border border-slate-100 rounded-md p-2">
                    Cambiar el tipo actualiza el vínculo al catálogo y la etiqueta. Precio, promos, vigencia y cupos
                    siguen siendo solo de esta licencia.
                  </p>
                </div>
              </div>
            </div>

            <div className="border-b border-gray-200 pb-4">
              <div className="flex items-center space-x-2 mb-3">
                <CurrencyDollarIcon className="h-5 w-5 text-green-600" />
                <h3 className="text-lg font-medium text-gray-900">
                  2. Contrato de esta licencia (precio, promos)
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Precio *
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editForm.price}
                    onChange={(e) => handleEditFormChange('price', parseFloat(e.target.value))}
                    placeholder="0.00"
                    error={editFormErrors.price}
                    required
                  />
                </div>

                {/* Original Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Precio Original
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editForm.original_price}
                    onChange={(e) => handleEditFormChange('original_price', parseFloat(e.target.value))}
                    placeholder="0.00"
                    error={editFormErrors.original_price}
                  />
                </div>

                {/* Discount Percentage */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descuento (%)
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={editForm.discount_percentage}
                    onChange={(e) => handleEditFormChange('discount_percentage', parseFloat(e.target.value))}
                    placeholder="0"
                    error={editFormErrors.discount_percentage}
                  />
                </div>

                {/* Promotion Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre de Promoción
                  </label>
                  <Input
                    type="text"
                    value={editForm.promotion_name}
                    onChange={(e) => handleEditFormChange('promotion_name', e.target.value)}
                    placeholder="Ej: Black Friday 2024"
                    error={editFormErrors.promotion_name}
                  />
                </div>

                {/* Promotion Description */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción de Promoción
                  </label>
                  <textarea
                    value={editForm.promotion_description}
                    onChange={(e) => handleEditFormChange('promotion_description', e.target.value)}
                    placeholder="Descripción de la promoción..."
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            <div className="border-b border-gray-200 pb-4">
              <div className="flex items-center space-x-2 mb-3">
                <CogIcon className="h-5 w-5 text-purple-600" />
                <h3 className="text-lg font-medium text-gray-900">Estado, cupos y vigencia</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estado *
                  </label>
                  <select
                    value={editForm.status}
                    onChange={(e) => handleEditFormChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    {LICENSE_STATUS.map(status => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Máximo de Instalaciones *
                  </label>
                  <Input
                    type="number"
                    value={editForm.max_installations}
                    onChange={(e) => handleEditFormChange('max_installations', parseInt(e.target.value))}
                    placeholder="5"
                    error={editFormErrors.max_installations}
                    required
                    min="1"
                  />
                </div>

                <div className="md:col-span-2 space-y-3">
                  <div className="flex items-start gap-2">
                    <input
                      id="edit-perpetual-validity"
                      type="checkbox"
                      checked={editForm.perpetual_validity}
                      onChange={(e) =>
                        handleEditFormChange('perpetual_validity', e.target.checked)
                      }
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="edit-perpetual-validity" className="text-sm text-gray-800">
                      <span className="font-medium">Vigencia indefinida</span>
                      <span className="block text-gray-500 font-normal mt-0.5">
                        Sin fecha de vencimiento.
                      </span>
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Vigencia (días desde hoy)
                    </label>
                    <Input
                      type="number"
                      value={editForm.expiry_days}
                      onChange={(e) =>
                        handleEditFormChange(
                          'expiry_days',
                          parseInt(e.target.value, 10) || 1
                        )
                      }
                      placeholder="365"
                      error={editFormErrors.expiry_days}
                      min="1"
                      disabled={editForm.perpetual_validity}
                      className={editForm.perpetual_validity ? 'opacity-60' : ''}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {editForm.perpetual_validity
                        ? 'Desactivá la casilla para definir vigencia en días desde hoy.'
                        : 'Al guardar se fija la fecha de expiración desde hoy con estos días.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center space-x-2 mb-3">
                <DocumentTextIcon className="h-5 w-5 text-gray-600" />
                <h3 className="text-lg font-medium text-gray-900">Notas adicionales</h3>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notas
                </label>
                <textarea
                  value={editForm.notes}
                  onChange={(e) => handleEditFormChange('notes', e.target.value)}
                  placeholder="Notas adicionales sobre la licencia"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="2"
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 mt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={handleEditModalClose}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={updateLicenseMutation.isPending}
            >
              {updateLicenseMutation.isPending ? 'Actualizando...' : 'Actualizar Licencia'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* View License Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title="Detalles de la Licencia"
      >
        <div className="p-6">
          {selectedLicense && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Clave de Licencia</label>
                <p className="text-gray-900 font-mono">{selectedLicense.license_key}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Cliente</label>
                <p className="text-gray-900">{selectedLicense.client_name}</p>
                <p className="text-sm text-gray-500">{selectedLicense.client_email}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Software</label>
                <p className="text-gray-900">{selectedLicense.software_name} v{selectedLicense.software_version}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Tipo</label>
                <Badge variant={getTypeBadgeVariant(selectedLicense)}>
                  {getTypeLabel(selectedLicense)}
                </Badge>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Estado</label>
                <Badge variant={getStatusBadgeVariant(selectedLicense.status)}>
                  {getStatusLabel(selectedLicense.status)}
                </Badge>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Instalaciones</label>
                <p className="text-gray-900">{selectedLicense.current_installations}/{selectedLicense.max_installations}</p>
                <p className="text-sm text-gray-500">{selectedLicense.remaining_installations} restantes</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Fecha de Emisión</label>
                <p className="text-gray-900">{formatDate(selectedLicense.issued_date)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Vigencia</label>
                {selectedLicense.perpetual_validity || !selectedLicense.expiry_date ? (
                  <p className="text-gray-900">Indefinida</p>
                ) : (
                  <p className="text-gray-900">{formatDate(selectedLicense.expiry_date)}</p>
                )}
              </div>
              {selectedLicense.notes && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Notas</label>
                  <p className="text-gray-900">{selectedLicense.notes}</p>
                </div>
              )}
            </div>
          )}
          <div className="flex justify-end mt-6">
            <Button onClick={() => setIsViewModalOpen(false)}>
              Cerrar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Success Notification Modal */}
      <SuccessNotification
        isOpen={isSuccessModalOpen}
        onClose={() => {
          setIsSuccessModalOpen(false);
          setCreatedLicense(null);
          setCreatedLicenseClient(null);
          setCreatedLicenseSoftware(null);
        }}
        license={createdLicense}
        client={createdLicenseClient}
        software={createdLicenseSoftware}
      />
    </div>
  );
};
